import type { BoardModel } from "../domain/types";

// MacBook Air 13" 2014 (A1466) — carte 820-00165 / J113 (051-00385).
// Haswell, PAS de GPU discret. Charge ISL6259, SMC alimenté par PP3V42_G3H.
// Rails + tensions dérivés du SCHÉMA (le nom Apple encode tension+état) ;
// valeurs/pads à VALIDER sur boardview (FlexBV) avant usage atelier.
// À fusionner dans SEED_BOARDS (src/data/boards.seed.ts).

export const BOARD_820_00165: BoardModel = {
  id: "820-00165",
  brand: "Apple",
  model: 'MacBook Air 13" 2014 (A1466)',
  aliases: ["A1466", "MacBookAir6,2", "MacBookAir7,2", "J113", "051-00385", "820-00165"],
  updated_at: new Date().toISOString(),
  rails: [
    {
      name: "PPDCIN_G3H",
      state: "G3",
      nominal_v: 14.85,
      tolerance_v: 0.8,
      source_component: "Connecteur MagSafe 2 → étage d'entrée (diode/MOSFET d'isolation)",
      parent: null,
      notes: "Tension d'entrée adaptateur (MagSafe 2 45 W ≈ 14.85 V). Absente sur batterie seule.",
    },
    {
      name: "PPBUS_G3H",
      state: "G3",
      nominal_v: 8.6,
      tolerance_v: 0.6,
      source_component: "Contrôleur de charge ISL6259 + MOSFETs de charge",
      enabled_by: "SMC",
      parent: "PPDCIN_G3H",
      notes: "Rail principal (batterie 2 cellules ≈ 8.6 V), présent adaptateur OU batterie. Point de départ de TOUT diagnostic d'alim.",
    },
    {
      name: "PP3V42_G3H",
      state: "always_on",
      nominal_v: 3.42,
      tolerance_v: 0.1,
      source_component: "Buck always-on depuis PPBUS_G3H",
      parent: "PPBUS_G3H",
      notes: "Alimente le SMC et la logique always-on. Absent → pas de réveil possible (carte morte).",
    },
    {
      name: "PPVRTC_G3H",
      state: "always_on",
      nominal_v: 3.0,
      tolerance_v: 0.3,
      source_component: "LDO RTC depuis PP3V42_G3H (+ pile/super-cap)",
      parent: "PP3V42_G3H",
      notes: "Horloge temps réel / always-on.",
    },
    {
      name: "PP5V_S5",
      state: "S5",
      nominal_v: 5.0,
      tolerance_v: 0.2,
      source_component: "Buck 5 V",
      enabled_by: "SMC (sortie de veille)",
      parent: "PPBUS_G3H",
      notes: "Apparaît à l'appui power. Absent → séquence bloquée au SMC.",
    },
    {
      name: "PP3V3_S5",
      state: "S5",
      nominal_v: 3.3,
      tolerance_v: 0.1,
      source_component: "Buck 3.3 V S5",
      parent: "PP5V_S5",
    },
    {
      name: "PP3V3_S4",
      state: "S4",
      nominal_v: 3.3,
      tolerance_v: 0.1,
      source_component: "Buck/commutation 3.3 V S4",
      parent: "PP5V_S5",
    },
    {
      name: "PP1V8_S3",
      state: "S3",
      nominal_v: 1.8,
      tolerance_v: 0.08,
      source_component: "Buck/LDO 1.8 V S3",
      parent: "PP3V3_S5",
    },
    {
      name: "PP1V2_S3",
      state: "S3",
      nominal_v: 1.2,
      tolerance_v: 0.06,
      source_component: "Buck 1.2 V S3 (DDR)",
      parent: "PP3V3_S5",
      notes: "Alim mémoire (avec PP0V6 VTT/VREF).",
    },
    {
      name: "PP1V05_S0",
      state: "S0",
      nominal_v: 1.05,
      tolerance_v: 0.05,
      source_component: "Buck 1.05 V (PCH/SoC)",
      parent: "PP3V3_S5",
    },
    {
      name: "PP5V_S0",
      state: "S0",
      nominal_v: 5.0,
      tolerance_v: 0.2,
      source_component: "Buck 5 V S0",
      parent: "PP5V_S5",
    },
    {
      name: "PP3V3_S0",
      state: "S0",
      nominal_v: 3.3,
      tolerance_v: 0.1,
      source_component: "Buck 3.3 V S0",
      parent: "PP3V3_S5",
    },
    {
      name: "PPVCC_S0_CPU",
      state: "S0",
      nominal_v: 0.95,
      tolerance_v: 0.15,
      source_component: "VR cœur CPU (IMVP, PMIC multiphase)",
      enabled_by: "Séquence S0 (CPU)",
      parent: "PPBUS_G3H",
      notes: "Tension cœur CPU (VID variable). Présente seulement en marche (S0).",
    },
    {
      name: "PPHV_S0SW_LCDBKLT",
      state: "S0",
      nominal_v: 8.6,
      tolerance_v: 0.6,
      source_component: "Load switch depuis PPBUS vers le rétroéclairage",
      parent: "PPBUS_G3H",
      notes: "Alim haute tension du rétroéclairage écran. Absente = pas d'image (écran noir mais carte vivante).",
    },
  ],
  measure_points: [
    {
      label: "Entrée adaptateur PPDCIN_G3H",
      rail: "PPDCIN_G3H",
      type: "voltage",
      expected: "≈ 14.85 V (MagSafe 2 branché)",
      location: "Étage d'entrée près de l'ISL6259",
      notes: "Absente sur batterie seule — normal.",
    },
    {
      label: "Bobine/shunt PPBUS_G3H",
      rail: "PPBUS_G3H",
      type: "voltage",
      expected: "8.0 – 8.7 V (selon charge batterie)",
      notes: "Si 0 V → remonter vers MOSFETs/ISL6259. Présent adaptateur OU batterie.",
    },
    {
      label: "PPBUS_G3H vers GND (hors tension)",
      rail: "PPBUS_G3H",
      type: "resistance",
      expected: "> quelques centaines d'Ω (pas de court franc)",
      notes: "< 5 Ω = court sur le rail principal → injection douce + caméra thermique.",
    },
    {
      label: "PP3V42_G3H",
      rail: "PP3V42_G3H",
      type: "voltage",
      expected: "3.35 – 3.5 V",
      notes: "Always-on (alim SMC). Absent → aucun réveil possible.",
    },
    {
      label: "PPVRTC_G3H",
      rail: "PPVRTC_G3H",
      type: "voltage",
      expected: "≈ 2.7 – 3.3 V",
    },
    {
      label: "PP5V_S5",
      rail: "PP5V_S5",
      type: "voltage",
      expected: "4.85 – 5.15 V",
      notes: "Apparaît à l'appui power. Absent → séquence bloquée (SMC/EN buck 5 V).",
    },
    {
      label: "PP3V3_S5",
      rail: "PP3V3_S5",
      type: "voltage",
      expected: "3.2 – 3.4 V",
    },
  ],
  symptoms: [
    {
      id: "dead_board",
      label: "Carte morte (aucun démarrage)",
      description: "Aucune réaction, pas de consommation à l'allumage.",
      checklist: [
        "Mesurer PPDCIN_G3H (adaptateur branché) ≈ 14.85 V",
        "Mesurer PPBUS_G3H — rail principal (≈ 8.6 V)",
        "Si PPBUS absent : vérifier MOSFETs de charge + ISL6259",
        "Mesurer PP3V42_G3H (alim SMC)",
        "Mesurer PPVRTC_G3H",
        "Appui power → PP5V_S5 doit apparaître",
      ],
    },
    {
      id: "no_charge",
      label: "Ne charge pas",
      description: "Batterie non chargée / adaptateur non reconnu (LED MagSafe).",
      checklist: [
        "Mesurer PPDCIN_G3H — présence tension d'entrée",
        "Vérifier ISL6259 (alim, ENABLE) et ses MOSFETs",
        "Contrôler la ligne de données MagSafe (LED) et les résistances de sense",
      ],
    },
    {
      id: "overcurrent",
      label: "Surconsommation / chauffe",
      description: "Consommation anormale, composant qui chauffe.",
      checklist: [
        "Mesurer résistance PPBUS_G3H vers GND",
        "Injection de tension douce + caméra thermique pour localiser le court",
        "Isoler les rails aval (S5/S3/S0) un par un",
      ],
    },
    {
      id: "no_video",
      label: "Pas d'image",
      description: "Démarre (ventilo/consommation) mais écran noir.",
      checklist: [
        "Confirmer rétroéclairage (lampe torche sur l'écran)",
        "Mesurer PPHV_S0SW_LCDBKLT (alim rétroéclairage)",
        "Contrôler le connecteur / la nappe LVDS-eDP",
        "Tester une sortie externe (Thunderbolt) pour isoler carte vs dalle",
      ],
    },
    {
      id: "no_boot_seq",
      label: "Bloque sur la séquence d'alim",
      description: "PPBUS et PP3V42 présents mais ne démarre pas (pas de S0).",
      checklist: [
        "Vérifier l'apparition de PP5V_S5 / PP3V3_S5 à l'appui power",
        "Suivre la séquence S5 → S3 → S0 (PP1V8_S3, PP1V2_S3, puis PP1V05_S0)",
        "Mesurer PPVCC_S0_CPU (cœur CPU) en marche",
        "Vérifier les signaux SMC / PM_SLP et les rails de la mémoire (PP0V6 VTT)",
      ],
    },
  ],
};
